package com.evoapps.evoshift.voice

import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.usecase.shift.ClockException
import com.evoapps.evoshift.domain.usecase.shift.ClockStatus
import com.evoapps.evoshift.widget.WatchClockSync
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.plus

/**
 * SH-150: what a voice assistant can ask of the app (Siri App Shortcuts, Google Assistant app
 * actions and the Android shortcuts; docs/voice.md). Each has an app link
 * `evoshift://app/voice/<path>` (navigation/DeepLinks.kt) that opens the voice screen, where the
 * answer is shown and read out, and a clock action waits for the person's confirmation.
 */
enum class VoiceAction(val path: String) {
    NEXT_SHIFT("next-shift"),
    WORK_TOMORROW("work-tomorrow"),
    CLOCK_IN("clock-in"),
    CLOCK_OUT("clock-out"),
    START_BREAK("start-break"),
    END_BREAK("end-break");

    /** Changes the shift (needs a confirmation and an unlocked device); the others only answer. */
    val isClock: Boolean get() = clockAction != null

    val isBreak: Boolean get() = this == START_BREAK || this == END_BREAK

    /** The action name of [WatchClockSync.perform] (the Apple Watch's contract, reused by Siri). */
    val clockAction: String?
        get() = when (this) {
            CLOCK_IN -> WatchClockSync.CLOCK_IN
            CLOCK_OUT -> WatchClockSync.CLOCK_OUT
            START_BREAK -> WatchClockSync.START_BREAK
            END_BREAK -> WatchClockSync.END_BREAK
            else -> null
        }

    val url: String get() = "$URL_PREFIX$path"

    companion object {
        const val URL_PREFIX = "evoshift://app/voice/"

        fun fromPath(path: String): VoiceAction? = entries.firstOrNull { it.path == path.trim('/') }

        fun fromClockAction(action: String): VoiceAction? = entries.firstOrNull { it.clockAction == action }
    }
}

/**
 * SH-150: the rules of the voice actions, without the UI (tested in VoiceRulesTest). Only the
 * person's own shifts are ever looked at.
 */
object VoiceRules {
    /**
     * Why [action] cannot be done now ([status] is the dashboard's clock button), or null when it
     * can. The texts are the app's own ([ClockException] reasons).
     */
    fun refusal(action: VoiceAction, status: ClockStatus): ClockException.Reason? = when (action) {
        VoiceAction.CLOCK_IN -> when (status) {
            is ClockStatus.CanClockIn -> null
            is ClockStatus.Working -> ClockException.Reason.ALREADY_CLOCKED_IN
            is ClockStatus.Done -> ClockException.Reason.ALREADY_DONE
            ClockStatus.Idle -> ClockException.Reason.NO_SHIFT_NOW
        }
        VoiceAction.CLOCK_OUT -> when (status) {
            is ClockStatus.Working -> null
            is ClockStatus.Done -> ClockException.Reason.ALREADY_DONE
            else -> ClockException.Reason.NOT_CLOCKED_IN
        }
        VoiceAction.START_BREAK, VoiceAction.END_BREAK -> when {
            status is ClockStatus.Done -> ClockException.Reason.ALREADY_DONE
            status !is ClockStatus.Working -> ClockException.Reason.NOT_CLOCKED_IN
            action == VoiceAction.START_BREAK && status.onBreak -> ClockException.Reason.ALREADY_ON_BREAK
            action == VoiceAction.END_BREAK && !status.onBreak -> ClockException.Reason.NOT_ON_BREAK
            else -> null
        }
        VoiceAction.NEXT_SHIFT, VoiceAction.WORK_TOMORROW -> null
    }

    /** The shift [status] is about (to clock in, or being worked). */
    fun shiftOf(status: ClockStatus): Shift? = when (status) {
        is ClockStatus.CanClockIn -> status.shift
        is ClockStatus.Working -> status.shift
        is ClockStatus.Done -> status.shift
        ClockStatus.Idle -> null
    }

    /**
     * The person's own published shifts that have not ended at [now], soonest first: the in
     * progress one, else the next (the widget's and the watch's rule).
     */
    fun upcoming(shifts: List<Shift>, userId: String, now: LocalDateTime): List<Shift> {
        val nowText = now.toString()
        return shifts
            .filter { it.isPublished && it.userId == userId && it.plannedTime.start.isNotEmpty() }
            .filter { it.plannedTime.end.ifEmpty { it.plannedTime.start } > nowText }
            .sortedBy { it.plannedTime.start }
    }

    fun nextShift(shifts: List<Shift>, userId: String, now: LocalDateTime): VoiceAnswer {
        val shift = upcoming(shifts, userId, now).firstOrNull() ?: return VoiceAnswer.NoUpcomingShift
        return VoiceAnswer.NextShift(shift, ongoing = shift.plannedTime.start <= now.toString())
    }

    /** The person's own published shifts starting tomorrow (the day after [now]). */
    fun tomorrow(shifts: List<Shift>, userId: String, now: LocalDateTime): VoiceAnswer {
        val tomorrow = now.date.plus(DatePeriod(days = 1)).toString()
        val mine = shifts
            .filter { it.isPublished && it.userId == userId && it.plannedTime.start.startsWith(tomorrow) }
            .sortedBy { it.plannedTime.start }
        return if (mine.isEmpty()) VoiceAnswer.FreeTomorrow else VoiceAnswer.WorkTomorrow(mine)
    }

    /** The workplace's name as the person's profile keeps it (no extra read). */
    fun workplaceName(shift: Shift, user: User): String? =
        user.engagements[RosterIds.workplaceIdOf(shift.rosterId)]?.cachedWorkplaceName?.takeIf { it.isNotBlank() }

    /** "08:00" from a local ISO date-time. */
    fun time(isoDateTime: String): String = isoDateTime.substringAfter('T', "").take(5)
}

/** What the assistant answers about the person's own schedule. */
sealed interface VoiceAnswer {
    data class NextShift(val shift: Shift, val ongoing: Boolean) : VoiceAnswer
    data object NoUpcomingShift : VoiceAnswer
    data class WorkTomorrow(val shifts: List<Shift>) : VoiceAnswer
    data object FreeTomorrow : VoiceAnswer
}

/**
 * SH-150: Android's ways in (androidApp: res/xml/shortcuts.xml, MainActivity). The shortcuts and
 * the Assistant's custom intents open the app link itself; the "open app feature" built-in intent
 * passes the matching shortcut's id (= [VoiceAction.path]) in [FEATURE_EXTRA].
 */
object VoiceLinks {
    const val FEATURE_EXTRA = "feature"

    /** The app link for a feature name: a shortcut id ("clock-in") or its spoken form ("clock in"). */
    fun urlForFeature(feature: String): String? {
        val path = feature.trim().lowercase().replace('_', '-').replace(' ', '-')
        return VoiceAction.fromPath(path)?.url
    }
}
